import { useEffect, useState } from "react";

import { useRun } from "@/api/queries";
import type { CapabilityInfo, Run } from "@/api/types";
import { Button } from "@/components/Button";
import { DefinitionList } from "@/components/DefinitionList";
import { Modal } from "@/components/Modal";
import { RemoteState } from "@/components/RemoteState";
import { RUN_FINAL_STATUSES, RunStateLabel, StatusLabel } from "@/components/StatusLabel";
import { formatValue } from "@/lib/artifact-content";
import { formatNumber } from "@/lib/cn";
import { isDemoProvider, providerLabel } from "@/lib/model-runtime";

/** 进行中运行的轮询间隔。 */
const POLL_MS = 3_000;

/**
 * 轮询停止条件——**不同于** `RUN_FINAL_STATUSES`。
 *
 * 后者回答的是「还能不能取消」，只含 `APPROVED` / `REJECTED` / `CANCELLED`；
 * 而 `RETRYABLE_FAILED` 与 `BLOCKED` 虽然不可取消，状态同样不会再自行变化，
 * 继续轮询只是白跑请求。两个判据用途不同，因此各用各的集合。
 */
const SETTLED_STATUSES = new Set([...RUN_FINAL_STATUSES, "RETRYABLE_FAILED", "BLOCKED"]);

export interface RunDetailProps {
  runId: string | null;
  /** 能力目录：用于把 `capability` 渲染成人类可读标签（禁止在此硬编码能力名）。 */
  catalog?: CapabilityInfo[];
  onClose: () => void;
}

/**
 * 运行详情（specs/14 §4.6 · `GET /runs/{id}`）。
 *
 * `GET /projects/{id}/runs` 与 `GET /runs/{id}` 返回**同构**的 `RunOut`，
 * 所以这里不是"取到列表没有的字段"，而是：**只轮询这一条**。
 * 进行中的运行需要反复刷新，重取整张生成任务列表既浪费也会刷新无关行的交互状态。
 *
 * 任务名与本次读入的产物都在定义列表里展示：`RunOut.task` / `RunOut.artifact_ids`（ADR-015）。
 * 写入元数据同理——`RunOut.write_target` / `logical_key` / `title`（ADR-016）：重试**据此重放**，
 * 所以必须可见；否则"重试写到哪"只能靠产物类型反推，而那正是被静默换掉的东西。
 * 下方「输入」一节因此只渲染**调用方自由输入**的参数，框架键不在其中。
 */
export function RunDetail({ runId, catalog = [], onClose }: RunDetailProps) {
  const [pollMs, setPollMs] = useState<number | undefined>(POLL_MS);
  const run = useRun(runId, pollMs);

  const status = run.data?.status;
  useEffect(() => {
    if (status && SETTLED_STATUSES.has(status)) setPollMs(undefined);
  }, [status]);

  const label =
    catalog.find((item) => item.id === run.data?.capability)?.label ?? run.data?.capability ?? "—";

  return (
    <Modal
      open={runId !== null}
      onClose={onClose}
      className="modal-wide"
      eyebrow="运行详情"
      title={label}
      footer={
        <Button variant="quiet" onClick={onClose}>
          关闭
        </Button>
      }
    >
      <RemoteState queries={[run]} title="运行详情加载失败">
        {run.data ? <RunBody run={run.data} /> : null}
      </RemoteState>
    </Modal>
  );
}

function RunBody({ run }: { run: Run }) {
  const running = !SETTLED_STATUSES.has(run.status);
  const inputs = Object.entries(run.inputs);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="meta-row">
        <RunStateLabel status={run.status} />
        {running ? <StatusLabel tone="info">每 {POLL_MS / 1000} 秒刷新</StatusLabel> : null}
        <StatusLabel tone={run.retry_count > 0 ? "warning" : "default"}>
          重试 {run.retry_count} 次
        </StatusLabel>
      </div>

      {/*
        假产出的警告放在最前面，不塞进定义列表的某一格。
        理由是这个事实会改变用户对**整份产出**的判断（它还会真的写入一个 draft 产物、
        计入额度），而定义列表里的一格在视觉上与"耗时""Token"同级。
      */}
      {isDemoProvider(run.provider) ? (
        <p className="inline-note" role="status">
          这次产出由确定性假模型生成，不是真实模型调用。
        </p>
      ) : null}

      <DefinitionList
        entries={[
          { term: "任务", value: run.task || "—" },
          { term: "写入目标", value: run.write_target || "—" },
          { term: "逻辑键", value: run.logical_key || "—" },
          { term: "标题", value: run.title || "—" },
          { term: "运行 ID", value: run.id },
          { term: "幂等键", value: run.run_key },
          {
            term: "读入产物",
            value: run.artifact_ids.length > 0 ? run.artifact_ids.join(" · ") : "—",
          },
          { term: "模型", value: run.model ?? "—" },
          // 与「模型」**分开一行**，不合并：`model` 记的是请求里的模型名，而假 provider
          // 会把它原样回显——合并就等于把"真调用"与"假产出"显示成同一个样子。
          { term: "模型来源", value: providerLabel(run.provider) },
          { term: "额度", value: formatNumber(run.cost_units) },
          { term: "方法包", value: run.methodology_version ?? "—" },
          { term: "Profile", value: run.profile_version ?? "—" },
          { term: "创建时间", value: run.created_at ?? "—" },
          { term: "完成时间", value: run.finished_at ?? "—" },
          { term: "耗时", value: run.latency_ms > 0 ? `${formatNumber(run.latency_ms)} ms` : "—" },
          {
            term: "Token",
            value: `${formatNumber(run.prompt_tokens)} 提示 / ${formatNumber(run.completion_tokens)} 生成`,
          },
          { term: "上下文", value: `${formatNumber(run.context_chars)} 字符` },
        ]}
      />

      {run.error ? (
        <div className="inline-note" style={{ color: "var(--red)" }}>
          失败原因：{run.error}
        </div>
      ) : null}

      <section>
        <h3>输入</h3>
        {inputs.length === 0 ? (
          <p className="inline-note">这次运行没有附加输入参数。</p>
        ) : (
          <DefinitionList
            entries={inputs.map(([key, value]) => ({
              term: key,
              value: formatValue(value, 200),
            }))}
          />
        )}
      </section>

      {Object.keys(run.outputs).length > 0 ? (
        <section>
          <h3>输出</h3>
          <DefinitionList
            entries={Object.entries(run.outputs).map(([key, value]) => ({
              term: key,
              value: formatValue(value, 200),
            }))}
          />
        </section>
      ) : null}

      {run.issues.length > 0 ? (
        <section>
          <h3>运行告警</h3>
          <ul className="issue-list">
            {run.issues.map((issue, index) => (
              <li className="issue-item" key={index}>
                <span className="mini-mark">!</span>
                <div>
                  <p>{formatValue(issue)}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
